import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { attachmentsApi, type Attachment } from '@/api/attachmentsApi'

export function useAttachments(routeId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['ticket-attachments', routeId],
    queryFn: () => attachmentsApi.list(routeId),
    enabled: enabled && Boolean(routeId),
  })
}

/** presign → PUT bytes → confirm. Returns the stored attachment. */
export function useUploadAttachment(routeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      file,
      visibility,
      messageId,
    }: {
      file: File
      visibility: 'customer' | 'internal'
      messageId?: string
    }): Promise<Attachment> => {
      const meta = { fileName: file.name, mimeType: file.type || 'application/octet-stream', sizeBytes: file.size }
      const presign = await attachmentsApi.presign(routeId, meta)
      await attachmentsApi.uploadBytes(presign, file)
      return attachmentsApi.confirm(routeId, { storageKey: presign.storageKey, ...meta, visibility, messageId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ticket-attachments', routeId] })
      void queryClient.invalidateQueries({ queryKey: ['ticket-messages', routeId] })
      void queryClient.invalidateQueries({ queryKey: ['ticket', routeId] })
    },
  })
}

/** Opens a presigned download URL in a new tab. */
export function useDownloadAttachment(routeId: string) {
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const { download } = await attachmentsApi.get(routeId, attachmentId)
      window.open(download.url, '_blank', 'noopener')
    },
  })
}
